import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { AccreditationStatus } from './accreditation/shared-types'

// ─── Local type definitions ──────────────────────────────────────────────────

type ANCCPendingAccreditation = {
  id: string
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
  courseCode: string | null
  expirationDate: string | null
  targetAudience: string[]
  educationalNeeds: string[]
  competency: string | null
  professionalSource: string | null
  notes: string | null
  submittedDate: string | null
  approvedDate: string | null
  rejectedDate: string | null
  createdTime: string
  updatedTime: string
}

type ANCCFinalizedAccreditation = {
  id: string
  courseCode: string
  expirationDate: string
  targetAudience: string[]
  educationalNeeds: string[]
  competency: string | null
  professionalSource: string | null
  createdTime: string | null
  updatedTime: string | null
}

type CAPCEPendingAccreditation = {
  id: string
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
  courseCode: string | null
  expirationDate: string | null
  applicationDate: string | null
  submissionDate: string | null
  topicArea: string | null
  classType: string | null
  allClearDate: string | null
  goLiveDate: string | null
  notes: string | null
  submittedDate: string | null
  approvedDate: string | null
  rejectedDate: string | null
  createdTime: string
  updatedTime: string
}

type CAPCEFinalizedAccreditation = {
  id: string
  courseCode: string
  expirationDate: string
  applicationDate: string
  submissionDate: string
  topicArea: string
  classType: string
  allClearDate: string | null
  goLiveDate: string | null
  createdTime: string
  updatedTime: string
}

type VirginiaPendingAccreditation = {
  id: string
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
  topic: string | null
  subTopic: string | null
  areaNumberALS: string | null
  areaNumberBLS: string | null
  expirationDate: string | null
  notes: string | null
  submittedDate: string | null
  approvedDate: string | null
  rejectedDate: string | null
  createdTime: string
  updatedTime: string
}

type VirginiaFinalizedAccreditation = {
  id: string
  topic: string
  subTopic: string
  areaNumberALS: string
  areaNumberBLS: string
  expirationDate: string | null
  createdTime: string
  updatedTime: string
}

type CEBrokerPendingAccreditation = {
  id: string
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
  courseCode: string | null
  professionCode: string | null
  subjectArea: string | null
  state: string | null
  expirationDate: string | null
  notes: string | null
  submittedDate: string | null
  approvedDate: string | null
  rejectedDate: string | null
  createdTime: string
  updatedTime: string
}

type CEBrokerFinalizedAccreditation = {
  id: string
  courseCode: string
  professionCode: string
  subjectArea: string
  state: string
  expirationDate: string | null
  createdTime: string
  updatedTime: string
}

type NewJerseyPendingAccreditation = {
  id: string
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
  courseCode: string | null
  expirationDate: string | null
  notes: string | null
  submittedDate: string | null
  approvedDate: string | null
  rejectedDate: string | null
  createdTime: string
  updatedTime: string
}

type NewJerseyFinalizedAccreditation = {
  id: string
  courseCode: string
  expirationDate: string | null
  createdTime: string
  updatedTime: string
}

type NewYorkPendingAccreditation = {
  id: string
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
  topicArea: string | null
  expirationDate: string | null
  notes: string | null
  submittedDate: string | null
  approvedDate: string | null
  rejectedDate: string | null
  createdTime: string
  updatedTime: string
}

type NewYorkFinalizedAccreditation = {
  id: string
  topicArea: string
  expirationDate: string | null
  createdTime: string
  updatedTime: string
}

// ─── Exported types ───────────────────────────────────────────────────────────

export type AccreditationOverview = {
  ancc: {
    finalized?: ANCCFinalizedAccreditation
    pending?: ANCCPendingAccreditation
  }
  capce: {
    finalized?: CAPCEFinalizedAccreditation
    pending?: CAPCEPendingAccreditation
  }
  virginia: {
    finalized?: VirginiaFinalizedAccreditation
    pending?: VirginiaPendingAccreditation
  }
  ceBroker: {
    kentucky: {
      finalized?: CEBrokerFinalizedAccreditation
      pending?: CEBrokerPendingAccreditation
    }
    tennessee: {
      finalized?: CEBrokerFinalizedAccreditation
      pending?: CEBrokerPendingAccreditation
    }
    florida: {
      finalized?: CEBrokerFinalizedAccreditation
      pending?: CEBrokerPendingAccreditation
    }
  }
  newJersey: {
    finalized?: NewJerseyFinalizedAccreditation
    pending?: NewJerseyPendingAccreditation
  }
  newYork: {
    finalized?: NewYorkFinalizedAccreditation
    pending?: NewYorkPendingAccreditation
  }
}

export type V1MigrationEntry = {
  courseID: string
  cAPCE: boolean
  virginia: boolean
  ceBrokerFlorida: boolean
  ceBrokerKentucky: boolean
  ceBrokerTennessee: boolean
  newJersey: boolean
  newYork: boolean
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export let fetchEventAccreditations = createAsyncThunk(
  'accreditation/fetchEventAccreditations',
  (eventID: string) =>
    axios
      .get<AccreditationOverview>(
        `/api/content-pipeline/accreditation-event/${eventID}/accreditations`,
      )
      .then(pluckData),
)

export let fetchCourseAccreditations = createAsyncThunk(
  'accreditation/fetchCourseAccreditations',
  (courseID: string) =>
    axios
      .get<AccreditationOverview>(
        `/api/content-pipeline/accreditation-course/${courseID}/accreditations`,
      )
      .then(pluckData),
)

export let fetchV1MigrationPendingIds = createAsyncThunk(
  'accreditation/fetchV1MigrationPendingIds',
  () =>
    axios
      .get<V1MigrationEntry[]>(
        '/api/content-pipeline/accreditation-course/migration/pending-v1-ids',
      )
      .then(pluckData),
)

export let migrateCourseAccreditations = createAsyncThunk(
  'accreditation/migrateCourseAccreditations',
  (courseId: string) =>
    axios
      .post<{
        courseID: string
        message: string
      }>(`/api/content-pipeline/accreditation-course/migrate/${courseId}`)
      .then(pluckData),
)

export let createPendingANCCEvent = createAsyncThunk(
  'accreditation/createPendingANCCEvent',
  (data: {
    eventId: string
    status: AccreditationStatus
    courseCode?: string | null
    expirationDate?: string | null
    targetAudience: string[]
    educationalNeeds: string[]
    competency?: string | null
    professionalSource?: string | null
    notes?: string | null
  }) =>
    axios
      .post<ANCCPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-ancc',
        {
          eventID: data.eventId,
          status: data.status,
          courseCode: data.courseCode,
          expirationDate: data.expirationDate,
          targetAudience: data.targetAudience,
          educationalNeeds: data.educationalNeeds,
          competency: data.competency,
          professionalSource: data.professionalSource,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingANCCEvent = createAsyncThunk(
  'accreditation/updatePendingANCCEvent',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    expirationDate?: string | null
    targetAudience?: string[]
    educationalNeeds?: string[]
    competency?: string | null
    professionalSource?: string | null
    notes?: string | null
  }) =>
    axios
      .put<ANCCPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-ancc/${data.id}`,
        {
          status: data.status,
          courseCode: data.courseCode,
          expirationDate: data.expirationDate,
          targetAudience: data.targetAudience,
          educationalNeeds: data.educationalNeeds,
          competency: data.competency,
          professionalSource: data.professionalSource,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingANCCEvent = createAsyncThunk(
  'accreditation/deletePendingANCCEvent',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-ancc/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingANCCEvent = createAsyncThunk(
  'accreditation/finalizePendingANCCEvent',
  (data: { id: string; eventId: string }) =>
    axios
      .post<ANCCFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-ancc/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let createPendingANCCCourse = createAsyncThunk(
  'accreditation/createPendingANCCCourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    courseCode?: string | null
    expirationDate?: string | null
    targetAudience: string[]
    educationalNeeds: string[]
    competency?: string | null
    professionalSource?: string | null
    notes?: string | null
  }) =>
    axios
      .post<ANCCPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-ancc',
        {
          courseID: data.courseId,
          status: data.status,
          courseCode: data.courseCode,
          expirationDate: data.expirationDate,
          targetAudience: data.targetAudience,
          educationalNeeds: data.educationalNeeds,
          competency: data.competency,
          professionalSource: data.professionalSource,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingANCCCourse = createAsyncThunk(
  'accreditation/updatePendingANCCCourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    expirationDate?: string | null
    targetAudience?: string[]
    educationalNeeds?: string[]
    competency?: string | null
    professionalSource?: string | null
    notes?: string | null
  }) =>
    axios
      .put<ANCCPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-ancc/${data.id}`,
        {
          status: data.status,
          courseCode: data.courseCode,
          expirationDate: data.expirationDate,
          targetAudience: data.targetAudience,
          educationalNeeds: data.educationalNeeds,
          competency: data.competency,
          professionalSource: data.professionalSource,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingANCCCourse = createAsyncThunk(
  'accreditation/deletePendingANCCCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-ancc/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingANCCCourse = createAsyncThunk(
  'accreditation/finalizePendingANCCCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<ANCCFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-ancc/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

export let createPendingCAPCE = createAsyncThunk(
  'accreditation/createPendingCAPCE',
  (data: {
    eventId: string
    status: AccreditationStatus
    courseCode?: string | null
    topicArea: string
    classType: string
    applicationDate?: string | null
    submissionDate?: string | null
    expirationDate?: string | null
    allClearDate?: string | null
    goLiveDate?: string | null
    notes?: string | null
  }) => {
    // The API's pending CAPCE model has no GoLiveDate and rejects unknown members, so it isn't sent.
    // eslint-disable-next-line fp/no-rest-parameters, @typescript-eslint/no-unused-vars
    let { goLiveDate, ...body } = data
    return axios
      .post<CAPCEPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-capce',
        body,
      )
      .then(pluckData)
  },
)

export let updatePendingCAPCE = createAsyncThunk(
  'accreditation/updatePendingCAPCE',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    expirationDate?: string | null
    topicArea?: string | null
    classType?: string | null
    applicationDate?: string | null
    submissionDate?: string | null
    allClearDate?: string | null
    goLiveDate?: string | null
    notes?: string | null
  }) => {
    let updateData = {
      status: data.status,
      courseCode: data.courseCode,
      expirationDate: data.expirationDate,
      topicArea: data.topicArea,
      classType: data.classType,
      applicationDate: data.applicationDate,
      submissionDate: data.submissionDate,
      allClearDate: data.allClearDate,
      notes: data.notes,
    }
    return axios
      .put<CAPCEPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-capce/${data.id}`,
        updateData,
      )
      .then(pluckData)
  },
)

export let deletePendingCAPCE = createAsyncThunk(
  'accreditation/deletePendingCAPCE',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-capce/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCAPCE = createAsyncThunk(
  'accreditation/finalizePendingCAPCE',
  (data: { id: string; eventId: string }) =>
    axios
      .post<CAPCEFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-capce/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let createPendingVirginia = createAsyncThunk(
  'accreditation/createPendingVirginia',
  (data: {
    eventId: string
    status: AccreditationStatus
    topic: string
    subTopic: string
    areaNumberALS?: string | null
    areaNumberBLS?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<VirginiaPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-virginia',
        data,
      )
      .then(pluckData),
)

export let updatePendingVirginia = createAsyncThunk(
  'accreditation/updatePendingVirginia',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    topic?: string | null
    subTopic?: string | null
    areaNumberALS?: string | null
    areaNumberBLS?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) => {
    let updateData = {
      status: data.status,
      topic: data.topic,
      subTopic: data.subTopic,
      areaNumberALS: data.areaNumberALS,
      areaNumberBLS: data.areaNumberBLS,
      expirationDate: data.expirationDate,
      notes: data.notes,
    }
    return axios
      .put<VirginiaPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-virginia/${data.id}`,
        updateData,
      )
      .then(pluckData)
  },
)

export let deletePendingVirginia = createAsyncThunk(
  'accreditation/deletePendingVirginia',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-virginia/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingVirginia = createAsyncThunk(
  'accreditation/finalizePendingVirginia',
  (data: { id: string; eventId: string }) =>
    axios
      .post<VirginiaFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-virginia/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let createPendingCEBrokerKentucky = createAsyncThunk(
  'accreditation/createPendingCEBrokerKentucky',
  (data: {
    eventId: string
    status: AccreditationStatus
    courseCode?: string | null
    professionCode: string
    subjectArea: string
    state: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<CEBrokerPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-cebroker',
        data,
      )
      .then(pluckData),
)

export let updatePendingCEBrokerKentucky = createAsyncThunk(
  'accreditation/updatePendingCEBrokerKentucky',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    professionCode?: string | null
    subjectArea?: string | null
    state?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) => {
    let updateData = {
      status: data.status,
      courseCode: data.courseCode,
      professionCode: data.professionCode,
      subjectArea: data.subjectArea,
      state: data.state,
      expirationDate: data.expirationDate,
      notes: data.notes,
    }
    return axios
      .put<CEBrokerPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-cebroker/${data.id}`,
        updateData,
      )
      .then(pluckData)
  },
)

export let deletePendingCEBrokerKentucky = createAsyncThunk(
  'accreditation/deletePendingCEBrokerKentucky',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-cebroker/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCEBrokerKentucky = createAsyncThunk(
  'accreditation/finalizePendingCEBrokerKentucky',
  (data: { id: string; eventId: string }) =>
    axios
      .post<CEBrokerFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-cebroker/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let createPendingCEBrokerTennessee = createAsyncThunk(
  'accreditation/createPendingCEBrokerTennessee',
  (data: {
    eventId: string
    status: AccreditationStatus
    courseCode?: string | null
    professionCode: string
    subjectArea: string
    state: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<CEBrokerPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-cebroker',
        data,
      )
      .then(pluckData),
)

export let updatePendingCEBrokerTennessee = createAsyncThunk(
  'accreditation/updatePendingCEBrokerTennessee',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    professionCode?: string | null
    subjectArea?: string | null
    state?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) => {
    let updateData = {
      status: data.status,
      courseCode: data.courseCode,
      professionCode: data.professionCode,
      subjectArea: data.subjectArea,
      state: data.state,
      expirationDate: data.expirationDate,
      notes: data.notes,
    }
    return axios
      .put<CEBrokerPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-cebroker/${data.id}`,
        updateData,
      )
      .then(pluckData)
  },
)

export let deletePendingCEBrokerTennessee = createAsyncThunk(
  'accreditation/deletePendingCEBrokerTennessee',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-cebroker/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCEBrokerTennessee = createAsyncThunk(
  'accreditation/finalizePendingCEBrokerTennessee',
  (data: { id: string; eventId: string }) =>
    axios
      .post<CEBrokerFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-cebroker/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let createPendingCEBrokerFlorida = createAsyncThunk(
  'accreditation/createPendingCEBrokerFlorida',
  (data: {
    eventId: string
    status: AccreditationStatus
    courseCode?: string | null
    professionCode: string
    subjectArea: string
    state: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<CEBrokerPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-cebroker',
        data,
      )
      .then(pluckData),
)

export let updatePendingCEBrokerFlorida = createAsyncThunk(
  'accreditation/updatePendingCEBrokerFlorida',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    professionCode?: string | null
    subjectArea?: string | null
    state?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) => {
    let updateData = {
      status: data.status,
      courseCode: data.courseCode,
      professionCode: data.professionCode,
      subjectArea: data.subjectArea,
      state: data.state,
      expirationDate: data.expirationDate,
      notes: data.notes,
    }
    return axios
      .put<CEBrokerPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-cebroker/${data.id}`,
        updateData,
      )
      .then(pluckData)
  },
)

export let deletePendingCEBrokerFlorida = createAsyncThunk(
  'accreditation/deletePendingCEBrokerFlorida',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-cebroker/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCEBrokerFlorida = createAsyncThunk(
  'accreditation/finalizePendingCEBrokerFlorida',
  (data: { id: string; eventId: string }) =>
    axios
      .post<CEBrokerFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-cebroker/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let createPendingNewJersey = createAsyncThunk(
  'accreditation/createPendingNewJersey',
  (data: {
    eventId: string
    status: AccreditationStatus
    courseCode?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<NewJerseyPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-newjersey',
        data,
      )
      .then(pluckData),
)

export let updatePendingNewJersey = createAsyncThunk(
  'accreditation/updatePendingNewJersey',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) => {
    let updateData = {
      status: data.status,
      courseCode: data.courseCode,
      expirationDate: data.expirationDate,
      notes: data.notes,
    }
    return axios
      .put<NewJerseyPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-newjersey/${data.id}`,
        updateData,
      )
      .then(pluckData)
  },
)

export let deletePendingNewJersey = createAsyncThunk(
  'accreditation/deletePendingNewJersey',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-newjersey/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingNewJersey = createAsyncThunk(
  'accreditation/finalizePendingNewJersey',
  (data: { id: string; eventId: string }) =>
    axios
      .post<NewJerseyFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-newjersey/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let createPendingNewYork = createAsyncThunk(
  'accreditation/createPendingNewYork',
  (data: {
    eventId: string
    status: AccreditationStatus
    topicArea: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<NewYorkPendingAccreditation>(
        '/api/content-pipeline/accreditation-event/pending-newyork',
        data,
      )
      .then(pluckData),
)

export let updatePendingNewYork = createAsyncThunk(
  'accreditation/updatePendingNewYork',
  (data: {
    id: string
    eventId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    topicArea?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) => {
    let updateData = {
      status: data.status,
      topicArea: data.topicArea,
      expirationDate: data.expirationDate,
      notes: data.notes,
    }
    return axios
      .put<NewYorkPendingAccreditation>(
        `/api/content-pipeline/accreditation-event/pending-newyork/${data.id}`,
        updateData,
      )
      .then(pluckData)
  },
)

export let deletePendingNewYork = createAsyncThunk(
  'accreditation/deletePendingNewYork',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-event/pending-newyork/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingNewYork = createAsyncThunk(
  'accreditation/finalizePendingNewYork',
  (data: { id: string; eventId: string }) =>
    axios
      .post<NewYorkFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-event/finalize-newyork/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, eventId: data.eventId })),
)

export let migrateAccreditationsFromLiveClass = createAsyncThunk(
  'accreditation/migrateAccreditationsFromLiveClass',
  (data: { eventName: string }) =>
    axios
      .post(
        '/api/content-pipeline/accreditation-event/migrate-from-liveclass',
        {
          eventName: data.eventName,
        },
      )
      .then(() => data.eventName),
)

export let createPendingCAPCECourse = createAsyncThunk(
  'accreditation/createPendingCAPCECourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    courseCode?: string | null
    topicArea: string
    classType: string
    applicationDate?: string | null
    submissionDate?: string | null
    expirationDate?: string | null
    allClearDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<CAPCEPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-capce',
        {
          courseID: data.courseId,
          status: data.status,
          courseCode: data.courseCode,
          topicArea: data.topicArea,
          classType: data.classType,
          applicationDate: data.applicationDate,
          submissionDate: data.submissionDate,
          expirationDate: data.expirationDate,
          allClearDate: data.allClearDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingCAPCECourse = createAsyncThunk(
  'accreditation/updatePendingCAPCECourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    topicArea?: string | null
    classType?: string | null
    applicationDate?: string | null
    submissionDate?: string | null
    expirationDate?: string | null
    allClearDate?: string | null
    notes?: string | null
  }) =>
    axios
      .put<CAPCEPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-capce/${data.id}`,
        {
          status: data.status,
          courseCode: data.courseCode,
          topicArea: data.topicArea,
          classType: data.classType,
          applicationDate: data.applicationDate,
          submissionDate: data.submissionDate,
          expirationDate: data.expirationDate,
          allClearDate: data.allClearDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingCAPCECourse = createAsyncThunk(
  'accreditation/deletePendingCAPCECourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-capce/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCAPCECourse = createAsyncThunk(
  'accreditation/finalizePendingCAPCECourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<CAPCEFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-capce/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

export let createPendingVirginiaCourse = createAsyncThunk(
  'accreditation/createPendingVirginiaCourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    topic: string
    subTopic: string
    areaNumberALS?: string | null
    areaNumberBLS?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<VirginiaPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-virginia',
        {
          courseID: data.courseId,
          status: data.status,
          topic: data.topic,
          subTopic: data.subTopic,
          areaNumberALS: data.areaNumberALS,
          areaNumberBLS: data.areaNumberBLS,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingVirginiaCourse = createAsyncThunk(
  'accreditation/updatePendingVirginiaCourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    topic?: string | null
    subTopic?: string | null
    areaNumberALS?: string | null
    areaNumberBLS?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .put<VirginiaPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-virginia/${data.id}`,
        {
          status: data.status,
          topic: data.topic,
          subTopic: data.subTopic,
          areaNumberALS: data.areaNumberALS,
          areaNumberBLS: data.areaNumberBLS,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingVirginiaCourse = createAsyncThunk(
  'accreditation/deletePendingVirginiaCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-virginia/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingVirginiaCourse = createAsyncThunk(
  'accreditation/finalizePendingVirginiaCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<VirginiaFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-virginia/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

export let createPendingCEBrokerKentuckyCourse = createAsyncThunk(
  'accreditation/createPendingCEBrokerKentuckyCourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    courseCode?: string | null
    professionCode: string
    subjectArea: string
    state: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<CEBrokerPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-cebroker',
        {
          courseID: data.courseId,
          status: data.status,
          courseCode: data.courseCode,
          professionCode: data.professionCode,
          subjectArea: data.subjectArea,
          state: data.state,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingCEBrokerKentuckyCourse = createAsyncThunk(
  'accreditation/updatePendingCEBrokerKentuckyCourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    professionCode?: string | null
    subjectArea?: string | null
    state?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .put<CEBrokerPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-cebroker/${data.id}`,
        {
          status: data.status,
          courseCode: data.courseCode,
          professionCode: data.professionCode,
          subjectArea: data.subjectArea,
          state: data.state,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingCEBrokerKentuckyCourse = createAsyncThunk(
  'accreditation/deletePendingCEBrokerKentuckyCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-cebroker/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCEBrokerKentuckyCourse = createAsyncThunk(
  'accreditation/finalizePendingCEBrokerKentuckyCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<CEBrokerFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-cebroker/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

export let createPendingCEBrokerTennesseeCourse = createAsyncThunk(
  'accreditation/createPendingCEBrokerTennesseeCourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    courseCode?: string | null
    professionCode: string
    subjectArea: string
    state: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<CEBrokerPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-cebroker',
        {
          courseID: data.courseId,
          status: data.status,
          courseCode: data.courseCode,
          professionCode: data.professionCode,
          subjectArea: data.subjectArea,
          state: data.state,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingCEBrokerTennesseeCourse = createAsyncThunk(
  'accreditation/updatePendingCEBrokerTennesseeCourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    professionCode?: string | null
    subjectArea?: string | null
    state?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .put<CEBrokerPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-cebroker/${data.id}`,
        {
          status: data.status,
          courseCode: data.courseCode,
          professionCode: data.professionCode,
          subjectArea: data.subjectArea,
          state: data.state,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingCEBrokerTennesseeCourse = createAsyncThunk(
  'accreditation/deletePendingCEBrokerTennesseeCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-cebroker/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCEBrokerTennesseeCourse = createAsyncThunk(
  'accreditation/finalizePendingCEBrokerTennesseeCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<CEBrokerFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-cebroker/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

export let createPendingCEBrokerFloridaCourse = createAsyncThunk(
  'accreditation/createPendingCEBrokerFloridaCourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    courseCode?: string | null
    professionCode: string
    subjectArea: string
    state: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<CEBrokerPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-cebroker',
        {
          courseID: data.courseId,
          status: data.status,
          courseCode: data.courseCode,
          professionCode: data.professionCode,
          subjectArea: data.subjectArea,
          state: data.state,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingCEBrokerFloridaCourse = createAsyncThunk(
  'accreditation/updatePendingCEBrokerFloridaCourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    professionCode?: string | null
    subjectArea?: string | null
    state?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .put<CEBrokerPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-cebroker/${data.id}`,
        {
          status: data.status,
          courseCode: data.courseCode,
          professionCode: data.professionCode,
          subjectArea: data.subjectArea,
          state: data.state,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingCEBrokerFloridaCourse = createAsyncThunk(
  'accreditation/deletePendingCEBrokerFloridaCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-cebroker/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingCEBrokerFloridaCourse = createAsyncThunk(
  'accreditation/finalizePendingCEBrokerFloridaCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<CEBrokerFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-cebroker/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

export let createPendingNewJerseyCourse = createAsyncThunk(
  'accreditation/createPendingNewJerseyCourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    courseCode?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<NewJerseyPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-newjersey',
        {
          courseID: data.courseId,
          status: data.status,
          courseCode: data.courseCode,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingNewJerseyCourse = createAsyncThunk(
  'accreditation/updatePendingNewJerseyCourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    courseCode?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .put<NewJerseyPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-newjersey/${data.id}`,
        {
          status: data.status,
          courseCode: data.courseCode,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingNewJerseyCourse = createAsyncThunk(
  'accreditation/deletePendingNewJerseyCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-newjersey/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingNewJerseyCourse = createAsyncThunk(
  'accreditation/finalizePendingNewJerseyCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<NewJerseyFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-newjersey/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

export let createPendingNewYorkCourse = createAsyncThunk(
  'accreditation/createPendingNewYorkCourse',
  (data: {
    courseId: string
    status: AccreditationStatus
    topicArea: string
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .post<NewYorkPendingAccreditation>(
        '/api/content-pipeline/accreditation-course/pending-newyork',
        {
          courseID: data.courseId,
          status: data.status,
          topicArea: data.topicArea,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let updatePendingNewYorkCourse = createAsyncThunk(
  'accreditation/updatePendingNewYorkCourse',
  (data: {
    id: string
    courseId: string
    status?: 'Draft' | 'Submitted' | 'Approved' | 'Rejected'
    topicArea?: string | null
    expirationDate?: string | null
    notes?: string | null
  }) =>
    axios
      .put<NewYorkPendingAccreditation>(
        `/api/content-pipeline/accreditation-course/pending-newyork/${data.id}`,
        {
          status: data.status,
          topicArea: data.topicArea,
          expirationDate: data.expirationDate,
          notes: data.notes,
        },
      )
      .then(pluckData),
)

export let deletePendingNewYorkCourse = createAsyncThunk(
  'accreditation/deletePendingNewYorkCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(
        `/api/content-pipeline/accreditation-course/pending-newyork/${data.id}`,
      )
      .then(() => data),
)

export let finalizePendingNewYorkCourse = createAsyncThunk(
  'accreditation/finalizePendingNewYorkCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .post<NewYorkFinalizedAccreditation>(
        `/api/content-pipeline/accreditation-course/finalize-newyork/${data.id}`,
      )
      .then(pluckData)
      .then((finalized) => ({ finalized, courseId: data.courseId })),
)

// ─── Delete finalized accreditation thunks ────────────────────────────────────

export let deleteANCCEvent = createAsyncThunk(
  'accreditation/deleteANCCEvent',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/ancc/${data.id}`)
      .then(() => data),
)

export let deleteANCCCourse = createAsyncThunk(
  'accreditation/deleteANCCCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/ancc/${data.id}`)
      .then(() => data),
)

export let deleteCAPCE = createAsyncThunk(
  'accreditation/deleteCAPCE',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/capce/${data.id}`)
      .then(() => data),
)

export let deleteCAPCECourse = createAsyncThunk(
  'accreditation/deleteCAPCECourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/capce/${data.id}`)
      .then(() => data),
)

export let deleteVirginia = createAsyncThunk(
  'accreditation/deleteVirginia',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/virginia/${data.id}`)
      .then(() => data),
)

export let deleteVirginiaCourse = createAsyncThunk(
  'accreditation/deleteVirginiaCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/virginia/${data.id}`)
      .then(() => data),
)

export let deleteCEBrokerKentucky = createAsyncThunk(
  'accreditation/deleteCEBrokerKentucky',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/cebroker/${data.id}`)
      .then(() => data),
)

export let deleteCEBrokerKentuckyCourse = createAsyncThunk(
  'accreditation/deleteCEBrokerKentuckyCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/cebroker/${data.id}`)
      .then(() => data),
)

export let deleteCEBrokerTennessee = createAsyncThunk(
  'accreditation/deleteCEBrokerTennessee',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/cebroker/${data.id}`)
      .then(() => data),
)

export let deleteCEBrokerTennesseeCourse = createAsyncThunk(
  'accreditation/deleteCEBrokerTennesseeCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/cebroker/${data.id}`)
      .then(() => data),
)

export let deleteCEBrokerFlorida = createAsyncThunk(
  'accreditation/deleteCEBrokerFlorida',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/cebroker/${data.id}`)
      .then(() => data),
)

export let deleteCEBrokerFloridaCourse = createAsyncThunk(
  'accreditation/deleteCEBrokerFloridaCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/cebroker/${data.id}`)
      .then(() => data),
)

export let deleteNewJersey = createAsyncThunk(
  'accreditation/deleteNewJersey',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/newjersey/${data.id}`)
      .then(() => data),
)

export let deleteNewJerseyCourse = createAsyncThunk(
  'accreditation/deleteNewJerseyCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/newjersey/${data.id}`)
      .then(() => data),
)

export let deleteNewYork = createAsyncThunk(
  'accreditation/deleteNewYork',
  (data: { id: string; eventId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-event/newyork/${data.id}`)
      .then(() => data),
)

export let deleteNewYorkCourse = createAsyncThunk(
  'accreditation/deleteNewYorkCourse',
  (data: { id: string; courseId: string }) =>
    axios
      .delete(`/api/content-pipeline/accreditation-course/newyork/${data.id}`)
      .then(() => data),
)

// ─── State ────────────────────────────────────────────────────────────────────

type AccreditationState = {
  eventAccreditations: Record<string, AccreditationOverview>
  eventAccreditationsStatus: Record<string, Status>
  courseAccreditations: Record<string, AccreditationOverview>
  courseAccreditationsStatus: Record<string, Status>
  v1MigrationEntries: V1MigrationEntry[]
  v1MigrationStatus: Status
  anccOperationStatus: Status
  anccOperationError: string | null
  anccOperationSuccess: string | null
  capceOperationStatus: Status
  capceOperationError: string | null
  capceOperationSuccess: string | null
  virginiaOperationStatus: Status
  virginiaOperationError: string | null
  virginiaOperationSuccess: string | null
  ceBrokerKentuckyOperationStatus: Status
  ceBrokerKentuckyOperationError: string | null
  ceBrokerKentuckyOperationSuccess: string | null
  ceBrokerTennesseeOperationStatus: Status
  ceBrokerTennesseeOperationError: string | null
  ceBrokerTennesseeOperationSuccess: string | null
  ceBrokerFloridaOperationStatus: Status
  ceBrokerFloridaOperationError: string | null
  ceBrokerFloridaOperationSuccess: string | null
  newJerseyOperationStatus: Status
  newJerseyOperationError: string | null
  newJerseyOperationSuccess: string | null
  newYorkOperationStatus: Status
  newYorkOperationError: string
  newYorkOperationSuccess: string
}

export let initialState: AccreditationState = {
  eventAccreditations: {},
  eventAccreditationsStatus: {},
  courseAccreditations: {},
  courseAccreditationsStatus: {},
  v1MigrationEntries: [],
  v1MigrationStatus: 'uninitialized',
  anccOperationStatus: 'uninitialized',
  anccOperationError: null,
  anccOperationSuccess: null,
  capceOperationStatus: 'uninitialized',
  capceOperationError: null,
  capceOperationSuccess: null,
  virginiaOperationStatus: 'uninitialized',
  virginiaOperationError: null,
  virginiaOperationSuccess: null,
  ceBrokerKentuckyOperationStatus: 'uninitialized',
  ceBrokerKentuckyOperationError: null,
  ceBrokerKentuckyOperationSuccess: null,
  ceBrokerTennesseeOperationStatus: 'uninitialized',
  ceBrokerTennesseeOperationError: null,
  ceBrokerTennesseeOperationSuccess: null,
  ceBrokerFloridaOperationStatus: 'uninitialized',
  ceBrokerFloridaOperationError: null,
  ceBrokerFloridaOperationSuccess: null,
  newJerseyOperationStatus: 'uninitialized',
  newJerseyOperationError: null,
  newJerseyOperationSuccess: null,
  newYorkOperationStatus: 'uninitialized',
  newYorkOperationError: '',
  newYorkOperationSuccess: '',
}

// ─── Slice ────────────────────────────────────────────────────────────────────

export let accreditationSlice = createSlice({
  name: 'accreditation',
  initialState,
  reducers: {
    clearAnccOperationStatus: (state) => {
      state.anccOperationStatus = 'uninitialized'
      state.anccOperationError = null
      state.anccOperationSuccess = null
    },
    clearCapceOperationStatus: (state) => {
      state.capceOperationStatus = 'uninitialized'
      state.capceOperationError = null
      state.capceOperationSuccess = null
    },
    clearVirginiaOperationStatus: (state) => {
      state.virginiaOperationStatus = 'uninitialized'
      state.virginiaOperationError = null
      state.virginiaOperationSuccess = null
    },
    clearCEBrokerKentuckyOperationStatus: (state) => {
      state.ceBrokerKentuckyOperationStatus = 'uninitialized'
      state.ceBrokerKentuckyOperationError = null
      state.ceBrokerKentuckyOperationSuccess = null
    },
    clearCEBrokerTennesseeOperationStatus: (state) => {
      state.ceBrokerTennesseeOperationStatus = 'uninitialized'
      state.ceBrokerTennesseeOperationError = null
      state.ceBrokerTennesseeOperationSuccess = null
    },
    clearCEBrokerFloridaOperationStatus: (state) => {
      state.ceBrokerFloridaOperationStatus = 'uninitialized'
      state.ceBrokerFloridaOperationError = null
      state.ceBrokerFloridaOperationSuccess = null
    },
    clearNewJerseyOperationStatus: (state) => {
      state.newJerseyOperationStatus = 'uninitialized'
      state.newJerseyOperationError = null
      state.newJerseyOperationSuccess = null
    },
    clearNewYorkOperationStatus: (state) => {
      state.newYorkOperationStatus = 'uninitialized'
      state.newYorkOperationError = ''
      state.newYorkOperationSuccess = ''
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchEventAccreditations
      .addCase(fetchEventAccreditations.pending, (state, action) => {
        state.eventAccreditationsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchEventAccreditations.rejected, (state, action) => {
        state.eventAccreditationsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventAccreditations.fulfilled, (state, action) => {
        state.eventAccreditationsStatus[action.meta.arg] = 'fulfilled'
        state.eventAccreditations[action.meta.arg] = {
          ...action.payload,
          ancc: action.payload.ancc ?? {},
        }
      })
      // fetchCourseAccreditations
      .addCase(fetchCourseAccreditations.pending, (state, action) => {
        state.courseAccreditationsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseAccreditations.rejected, (state, action) => {
        state.courseAccreditationsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseAccreditations.fulfilled, (state, action) => {
        state.courseAccreditationsStatus[action.meta.arg] = 'fulfilled'
        state.courseAccreditations[action.meta.arg] = {
          ...action.payload,
          ancc: action.payload.ancc ?? {},
        }
      })
      // fetchV1MigrationPendingIds
      .addCase(fetchV1MigrationPendingIds.pending, (state) => {
        state.v1MigrationStatus = 'pending'
      })
      .addCase(fetchV1MigrationPendingIds.rejected, (state) => {
        state.v1MigrationStatus = 'rejected'
      })
      .addCase(fetchV1MigrationPendingIds.fulfilled, (state, action) => {
        state.v1MigrationStatus = 'fulfilled'
        state.v1MigrationEntries = action.payload
      })
      // createPendingANCCEvent
      .addCase(createPendingANCCEvent.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(createPendingANCCEvent.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ancc.pending = action.payload
        }
      })
      .addCase(createPendingANCCEvent.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to create ANCC accreditation'
      })
      // updatePendingANCCEvent
      .addCase(updatePendingANCCEvent.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(updatePendingANCCEvent.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.anccOperationSuccess = 'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.anccOperationSuccess = 'Approved successfully'
        } else {
          state.anccOperationSuccess = 'ANCC accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ancc.pending = action.payload
        }
      })
      .addCase(updatePendingANCCEvent.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to update ANCC accreditation'
      })
      // deletePendingANCCEvent
      .addCase(deletePendingANCCEvent.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(deletePendingANCCEvent.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ancc.pending = undefined
        }
      })
      .addCase(deletePendingANCCEvent.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to delete ANCC accreditation'
      })
      // finalizePendingANCCEvent
      .addCase(finalizePendingANCCEvent.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(finalizePendingANCCEvent.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation finalized successfully'
        let { eventId, finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ancc.finalized = finalized
          state.eventAccreditations[eventId].ancc.pending = undefined
        }
      })
      .addCase(finalizePendingANCCEvent.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message ||
          'Failed to finalize ANCC. Ensure the status is Approved first.'
      })
      // createPendingANCCCourse
      .addCase(createPendingANCCCourse.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(createPendingANCCCourse.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation created successfully'
        let { courseId } = action.meta.arg
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ancc.pending = action.payload
        }
      })
      .addCase(createPendingANCCCourse.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to create ANCC accreditation'
      })
      // updatePendingANCCCourse
      .addCase(updatePendingANCCCourse.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(updatePendingANCCCourse.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        let { courseId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.anccOperationSuccess = 'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.anccOperationSuccess = 'Approved successfully'
        } else {
          state.anccOperationSuccess = 'ANCC accreditation updated successfully'
        }
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ancc.pending = action.payload
        }
      })
      .addCase(updatePendingANCCCourse.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to update ANCC accreditation'
      })
      // deletePendingANCCCourse
      .addCase(deletePendingANCCCourse.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(deletePendingANCCCourse.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ancc.pending = undefined
        }
      })
      .addCase(deletePendingANCCCourse.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to delete ANCC accreditation'
      })
      // finalizePendingANCCCourse
      .addCase(finalizePendingANCCCourse.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(finalizePendingANCCCourse.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation finalized successfully'
        let { courseId, finalized } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ancc.finalized = finalized
          state.courseAccreditations[courseId].ancc.pending = undefined
        }
      })
      .addCase(finalizePendingANCCCourse.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message ||
          'Failed to finalize ANCC. Ensure the status is Approved first.'
      })
      // createPendingCAPCE
      .addCase(createPendingCAPCE.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(createPendingCAPCE.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess = 'CAPCE accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].capce.pending = action.payload
        }
      })
      .addCase(createPendingCAPCE.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to create CAPCE accreditation'
      })
      // updatePendingCAPCE
      .addCase(updatePendingCAPCE.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(updatePendingCAPCE.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.capceOperationSuccess = 'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.capceOperationSuccess = 'Approved successfully'
        } else {
          state.capceOperationSuccess =
            'CAPCE accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].capce.pending = action.payload
        }
      })
      .addCase(updatePendingCAPCE.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to update CAPCE accreditation'
      })
      // deletePendingCAPCE
      .addCase(deletePendingCAPCE.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(deletePendingCAPCE.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess = 'CAPCE accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].capce.pending = undefined
        }
      })
      .addCase(deletePendingCAPCE.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to delete CAPCE accreditation'
      })
      // finalizePendingCAPCE
      .addCase(finalizePendingCAPCE.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(finalizePendingCAPCE.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess =
          'CAPCE accreditation finalized successfully'
        let { eventId } = action.payload
        let { finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].capce.finalized = finalized
          state.eventAccreditations[eventId].capce.pending = undefined
        }
      })
      .addCase(finalizePendingCAPCE.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message ||
          'Failed to finalize CAPCE. Ensure the status is Approved first.'
      })
      // createPendingVirginia
      .addCase(createPendingVirginia.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(createPendingVirginia.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].virginia.pending = action.payload
        }
      })
      .addCase(createPendingVirginia.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to create Virginia accreditation'
      })
      // updatePendingVirginia
      .addCase(updatePendingVirginia.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(updatePendingVirginia.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.virginiaOperationSuccess = 'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.virginiaOperationSuccess = 'Approved successfully'
        } else {
          state.virginiaOperationSuccess =
            'Virginia accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].virginia.pending = action.payload
        }
      })
      .addCase(updatePendingVirginia.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to update Virginia accreditation'
      })
      // deletePendingVirginia
      .addCase(deletePendingVirginia.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(deletePendingVirginia.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].virginia.pending = undefined
        }
      })
      .addCase(deletePendingVirginia.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to delete Virginia accreditation'
      })
      // finalizePendingVirginia
      .addCase(finalizePendingVirginia.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(finalizePendingVirginia.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation finalized successfully'
        let { eventId } = action.payload
        let { finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].virginia.finalized = finalized
          state.eventAccreditations[eventId].virginia.pending = undefined
        }
      })
      .addCase(finalizePendingVirginia.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message ||
          'Failed to finalize Virginia. Ensure the status is Approved first.'
      })
      // createPendingCEBrokerKentucky
      .addCase(createPendingCEBrokerKentucky.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(createPendingCEBrokerKentucky.fulfilled, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'fulfilled'
        state.ceBrokerKentuckyOperationSuccess =
          'CEBroker Kentucky accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.kentucky.pending =
            action.payload
        }
      })
      .addCase(createPendingCEBrokerKentucky.rejected, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'rejected'
        state.ceBrokerKentuckyOperationError =
          action.error.message ||
          'Failed to create CEBroker Kentucky accreditation'
      })
      // updatePendingCEBrokerKentucky
      .addCase(updatePendingCEBrokerKentucky.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(updatePendingCEBrokerKentucky.fulfilled, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.ceBrokerKentuckyOperationSuccess =
            'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.ceBrokerKentuckyOperationSuccess = 'Approved successfully'
        } else {
          state.ceBrokerKentuckyOperationSuccess =
            'CEBroker Kentucky accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.kentucky.pending =
            action.payload
        }
      })
      .addCase(updatePendingCEBrokerKentucky.rejected, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'rejected'
        state.ceBrokerKentuckyOperationError =
          action.error.message ||
          'Failed to update CEBroker Kentucky accreditation'
      })
      // deletePendingCEBrokerKentucky
      .addCase(deletePendingCEBrokerKentucky.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(deletePendingCEBrokerKentucky.fulfilled, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'fulfilled'
        state.ceBrokerKentuckyOperationSuccess =
          'CEBroker Kentucky accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.kentucky.pending =
            undefined
        }
      })
      .addCase(deletePendingCEBrokerKentucky.rejected, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'rejected'
        state.ceBrokerKentuckyOperationError =
          action.error.message ||
          'Failed to delete CEBroker Kentucky accreditation'
      })
      // finalizePendingCEBrokerKentucky
      .addCase(finalizePendingCEBrokerKentucky.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(finalizePendingCEBrokerKentucky.fulfilled, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'fulfilled'
        state.ceBrokerKentuckyOperationSuccess =
          'CEBroker Kentucky accreditation finalized successfully'
        let { eventId } = action.payload
        let { finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.kentucky.finalized =
            finalized
          state.eventAccreditations[eventId].ceBroker.kentucky.pending =
            undefined
        }
      })
      .addCase(finalizePendingCEBrokerKentucky.rejected, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'rejected'
        state.ceBrokerKentuckyOperationError =
          action.error.message ||
          'Failed to finalize CEBroker Kentucky. Ensure the status is Approved first.'
      })
      // createPendingCEBrokerTennessee
      .addCase(createPendingCEBrokerTennessee.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(createPendingCEBrokerTennessee.fulfilled, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'fulfilled'
        state.ceBrokerTennesseeOperationSuccess =
          'CEBroker Tennessee accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.tennessee.pending =
            action.payload
        }
      })
      .addCase(createPendingCEBrokerTennessee.rejected, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'rejected'
        state.ceBrokerTennesseeOperationError =
          action.error.message ||
          'Failed to create CEBroker Tennessee accreditation'
      })
      // updatePendingCEBrokerTennessee
      .addCase(updatePendingCEBrokerTennessee.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(updatePendingCEBrokerTennessee.fulfilled, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.ceBrokerTennesseeOperationSuccess =
            'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.ceBrokerTennesseeOperationSuccess = 'Approved successfully'
        } else {
          state.ceBrokerTennesseeOperationSuccess =
            'CEBroker Tennessee accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.tennessee.pending =
            action.payload
        }
      })
      .addCase(updatePendingCEBrokerTennessee.rejected, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'rejected'
        state.ceBrokerTennesseeOperationError =
          action.error.message ||
          'Failed to update CEBroker Tennessee accreditation'
      })
      // deletePendingCEBrokerTennessee
      .addCase(deletePendingCEBrokerTennessee.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(deletePendingCEBrokerTennessee.fulfilled, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'fulfilled'
        state.ceBrokerTennesseeOperationSuccess =
          'CEBroker Tennessee accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.tennessee.pending =
            undefined
        }
      })
      .addCase(deletePendingCEBrokerTennessee.rejected, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'rejected'
        state.ceBrokerTennesseeOperationError =
          action.error.message ||
          'Failed to delete CEBroker Tennessee accreditation'
      })
      // finalizePendingCEBrokerTennessee
      .addCase(finalizePendingCEBrokerTennessee.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(finalizePendingCEBrokerTennessee.fulfilled, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'fulfilled'
        state.ceBrokerTennesseeOperationSuccess =
          'CEBroker Tennessee accreditation finalized successfully'
        let { eventId } = action.payload
        let { finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.tennessee.finalized =
            finalized
          state.eventAccreditations[eventId].ceBroker.tennessee.pending =
            undefined
        }
      })
      .addCase(finalizePendingCEBrokerTennessee.rejected, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'rejected'
        state.ceBrokerTennesseeOperationError =
          action.error.message ||
          'Failed to finalize CEBroker Tennessee. Ensure the status is Approved first.'
      })
      // createPendingCEBrokerFlorida
      .addCase(createPendingCEBrokerFlorida.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(createPendingCEBrokerFlorida.fulfilled, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'fulfilled'
        state.ceBrokerFloridaOperationSuccess =
          'CEBroker Florida accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.florida.pending =
            action.payload
        }
      })
      .addCase(createPendingCEBrokerFlorida.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to create CEBroker Florida accreditation'
      })
      // updatePendingCEBrokerFlorida
      .addCase(updatePendingCEBrokerFlorida.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(updatePendingCEBrokerFlorida.fulfilled, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.ceBrokerFloridaOperationSuccess =
            'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.ceBrokerFloridaOperationSuccess = 'Approved successfully'
        } else {
          state.ceBrokerFloridaOperationSuccess =
            'CEBroker Florida accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.florida.pending =
            action.payload
        }
      })
      .addCase(updatePendingCEBrokerFlorida.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to update CEBroker Florida accreditation'
      })
      // deletePendingCEBrokerFlorida
      .addCase(deletePendingCEBrokerFlorida.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(deletePendingCEBrokerFlorida.fulfilled, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'fulfilled'
        state.ceBrokerFloridaOperationSuccess =
          'CEBroker Florida accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.florida.pending =
            undefined
        }
      })
      .addCase(deletePendingCEBrokerFlorida.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to delete CEBroker Florida accreditation'
      })
      // finalizePendingCEBrokerFlorida
      .addCase(finalizePendingCEBrokerFlorida.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(finalizePendingCEBrokerFlorida.fulfilled, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'fulfilled'
        state.ceBrokerFloridaOperationSuccess =
          'CEBroker Florida accreditation finalized successfully'
        let { eventId } = action.payload
        let { finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.florida.finalized =
            finalized
          state.eventAccreditations[eventId].ceBroker.florida.pending =
            undefined
        }
      })
      .addCase(finalizePendingCEBrokerFlorida.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to finalize CEBroker Florida. Ensure the status is Approved first.'
      })
      // createPendingNewJersey
      .addCase(createPendingNewJersey.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(createPendingNewJersey.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newJersey.pending = action.payload
        }
      })
      .addCase(createPendingNewJersey.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to create New Jersey accreditation'
      })
      // updatePendingNewJersey
      .addCase(updatePendingNewJersey.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(updatePendingNewJersey.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.newJerseyOperationSuccess =
            'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.newJerseyOperationSuccess = 'Approved successfully'
        } else {
          state.newJerseyOperationSuccess =
            'New Jersey accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newJersey.pending = action.payload
        }
      })
      .addCase(updatePendingNewJersey.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to update New Jersey accreditation'
      })
      // deletePendingNewJersey
      .addCase(deletePendingNewJersey.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(deletePendingNewJersey.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newJersey.pending = undefined
        }
      })
      .addCase(deletePendingNewJersey.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to delete New Jersey accreditation'
      })
      // finalizePendingNewJersey
      .addCase(finalizePendingNewJersey.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(finalizePendingNewJersey.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation finalized successfully'
        let { eventId } = action.payload
        let { finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newJersey.finalized = finalized
          state.eventAccreditations[eventId].newJersey.pending = undefined
        }
      })
      .addCase(finalizePendingNewJersey.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message ||
          'Failed to finalize New Jersey. Ensure the status is Approved first.'
      })
      // createPendingNewYork
      .addCase(createPendingNewYork.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(createPendingNewYork.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation created successfully'
        let { eventId } = action.meta.arg
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newYork.pending = action.payload
        }
      })
      .addCase(createPendingNewYork.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to create New York accreditation'
      })
      // updatePendingNewYork
      .addCase(updatePendingNewYork.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(updatePendingNewYork.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        let { eventId } = action.meta.arg
        let { status } = action.meta.arg
        if (status === 'Submitted') {
          state.newYorkOperationSuccess = 'Submitted for approval successfully'
        } else if (status === 'Approved') {
          state.newYorkOperationSuccess = 'Approved successfully'
        } else {
          state.newYorkOperationSuccess =
            'New York accreditation updated successfully'
        }
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newYork.pending = action.payload
        }
      })
      .addCase(updatePendingNewYork.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to update New York accreditation'
      })
      // deletePendingNewYork
      .addCase(deletePendingNewYork.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(deletePendingNewYork.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newYork.pending = undefined
        }
      })
      .addCase(deletePendingNewYork.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to delete New York accreditation'
      })
      // finalizePendingNewYork
      .addCase(finalizePendingNewYork.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(finalizePendingNewYork.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation finalized successfully'
        let { eventId } = action.payload
        let { finalized } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newYork.finalized = finalized
          state.eventAccreditations[eventId].newYork.pending = undefined
        }
      })
      .addCase(finalizePendingNewYork.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message ||
          'Failed to finalize New York. Ensure the status is Approved first.'
      })
      // ── Course-side CAPCE ──────────────────────────────────────────────────
      .addCase(createPendingCAPCECourse.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(createPendingCAPCECourse.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess = 'CAPCE accreditation created successfully'
        let { courseId } = action.meta.arg
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].capce.pending = action.payload
        }
      })
      .addCase(createPendingCAPCECourse.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to create CAPCE accreditation'
      })
      .addCase(updatePendingCAPCECourse.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(updatePendingCAPCECourse.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        let { courseId, status } = action.meta.arg
        state.capceOperationSuccess =
          status === 'Submitted'
            ? 'Submitted for approval successfully'
            : status === 'Approved'
              ? 'Approved successfully'
              : 'CAPCE accreditation updated successfully'
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].capce.pending = action.payload
        }
      })
      .addCase(updatePendingCAPCECourse.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to update CAPCE accreditation'
      })
      .addCase(deletePendingCAPCECourse.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(deletePendingCAPCECourse.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess = 'CAPCE accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].capce.pending = undefined
        }
      })
      .addCase(deletePendingCAPCECourse.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to delete CAPCE accreditation'
      })
      .addCase(finalizePendingCAPCECourse.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(finalizePendingCAPCECourse.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess =
          'CAPCE accreditation finalized successfully'
        let { courseId, finalized } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].capce.finalized = finalized
          state.courseAccreditations[courseId].capce.pending = undefined
        }
      })
      .addCase(finalizePendingCAPCECourse.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message ||
          'Failed to finalize CAPCE. Ensure the status is Approved first.'
      })
      // ── Course-side Virginia ───────────────────────────────────────────────
      .addCase(createPendingVirginiaCourse.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(createPendingVirginiaCourse.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation created successfully'
        let { courseId } = action.meta.arg
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].virginia.pending = action.payload
        }
      })
      .addCase(createPendingVirginiaCourse.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to create Virginia accreditation'
      })
      .addCase(updatePendingVirginiaCourse.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(updatePendingVirginiaCourse.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        let { courseId, status } = action.meta.arg
        state.virginiaOperationSuccess =
          status === 'Submitted'
            ? 'Submitted for approval successfully'
            : status === 'Approved'
              ? 'Approved successfully'
              : 'Virginia accreditation updated successfully'
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].virginia.pending = action.payload
        }
      })
      .addCase(updatePendingVirginiaCourse.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to update Virginia accreditation'
      })
      .addCase(deletePendingVirginiaCourse.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(deletePendingVirginiaCourse.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].virginia.pending = undefined
        }
      })
      .addCase(deletePendingVirginiaCourse.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to delete Virginia accreditation'
      })
      .addCase(finalizePendingVirginiaCourse.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(finalizePendingVirginiaCourse.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation finalized successfully'
        let { courseId, finalized } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].virginia.finalized = finalized
          state.courseAccreditations[courseId].virginia.pending = undefined
        }
      })
      .addCase(finalizePendingVirginiaCourse.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message ||
          'Failed to finalize Virginia. Ensure the status is Approved first.'
      })
      // ── Course-side CEBroker Kentucky ──────────────────────────────────────
      .addCase(createPendingCEBrokerKentuckyCourse.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(
        createPendingCEBrokerKentuckyCourse.fulfilled,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'fulfilled'
          state.ceBrokerKentuckyOperationSuccess =
            'CEBroker Kentucky accreditation created successfully'
          let { courseId } = action.meta.arg
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.kentucky.pending =
              action.payload
          }
        },
      )
      .addCase(
        createPendingCEBrokerKentuckyCourse.rejected,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'rejected'
          state.ceBrokerKentuckyOperationError =
            action.error.message ||
            'Failed to create CEBroker Kentucky accreditation'
        },
      )
      .addCase(updatePendingCEBrokerKentuckyCourse.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(
        updatePendingCEBrokerKentuckyCourse.fulfilled,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'fulfilled'
          let { courseId, status } = action.meta.arg
          state.ceBrokerKentuckyOperationSuccess =
            status === 'Submitted'
              ? 'Submitted for approval successfully'
              : status === 'Approved'
                ? 'Approved successfully'
                : 'CEBroker Kentucky accreditation updated successfully'
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.kentucky.pending =
              action.payload
          }
        },
      )
      .addCase(
        updatePendingCEBrokerKentuckyCourse.rejected,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'rejected'
          state.ceBrokerKentuckyOperationError =
            action.error.message ||
            'Failed to update CEBroker Kentucky accreditation'
        },
      )
      .addCase(deletePendingCEBrokerKentuckyCourse.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(
        deletePendingCEBrokerKentuckyCourse.fulfilled,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'fulfilled'
          state.ceBrokerKentuckyOperationSuccess =
            'CEBroker Kentucky accreditation deleted successfully'
          let { courseId } = action.payload
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.kentucky.pending =
              undefined
          }
        },
      )
      .addCase(
        deletePendingCEBrokerKentuckyCourse.rejected,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'rejected'
          state.ceBrokerKentuckyOperationError =
            action.error.message ||
            'Failed to delete CEBroker Kentucky accreditation'
        },
      )
      .addCase(finalizePendingCEBrokerKentuckyCourse.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(
        finalizePendingCEBrokerKentuckyCourse.fulfilled,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'fulfilled'
          state.ceBrokerKentuckyOperationSuccess =
            'CEBroker Kentucky accreditation finalized successfully'
          let { courseId, finalized } = action.payload
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.kentucky.finalized =
              finalized
            state.courseAccreditations[courseId].ceBroker.kentucky.pending =
              undefined
          }
        },
      )
      .addCase(
        finalizePendingCEBrokerKentuckyCourse.rejected,
        (state, action) => {
          state.ceBrokerKentuckyOperationStatus = 'rejected'
          state.ceBrokerKentuckyOperationError =
            action.error.message ||
            'Failed to finalize CEBroker Kentucky. Ensure the status is Approved first.'
        },
      )
      // ── Course-side CEBroker Tennessee ─────────────────────────────────────
      .addCase(createPendingCEBrokerTennesseeCourse.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(
        createPendingCEBrokerTennesseeCourse.fulfilled,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'fulfilled'
          state.ceBrokerTennesseeOperationSuccess =
            'CEBroker Tennessee accreditation created successfully'
          let { courseId } = action.meta.arg
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.tennessee.pending =
              action.payload
          }
        },
      )
      .addCase(
        createPendingCEBrokerTennesseeCourse.rejected,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'rejected'
          state.ceBrokerTennesseeOperationError =
            action.error.message ||
            'Failed to create CEBroker Tennessee accreditation'
        },
      )
      .addCase(updatePendingCEBrokerTennesseeCourse.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(
        updatePendingCEBrokerTennesseeCourse.fulfilled,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'fulfilled'
          let { courseId, status } = action.meta.arg
          state.ceBrokerTennesseeOperationSuccess =
            status === 'Submitted'
              ? 'Submitted for approval successfully'
              : status === 'Approved'
                ? 'Approved successfully'
                : 'CEBroker Tennessee accreditation updated successfully'
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.tennessee.pending =
              action.payload
          }
        },
      )
      .addCase(
        updatePendingCEBrokerTennesseeCourse.rejected,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'rejected'
          state.ceBrokerTennesseeOperationError =
            action.error.message ||
            'Failed to update CEBroker Tennessee accreditation'
        },
      )
      .addCase(deletePendingCEBrokerTennesseeCourse.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(
        deletePendingCEBrokerTennesseeCourse.fulfilled,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'fulfilled'
          state.ceBrokerTennesseeOperationSuccess =
            'CEBroker Tennessee accreditation deleted successfully'
          let { courseId } = action.payload
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.tennessee.pending =
              undefined
          }
        },
      )
      .addCase(
        deletePendingCEBrokerTennesseeCourse.rejected,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'rejected'
          state.ceBrokerTennesseeOperationError =
            action.error.message ||
            'Failed to delete CEBroker Tennessee accreditation'
        },
      )
      .addCase(finalizePendingCEBrokerTennesseeCourse.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(
        finalizePendingCEBrokerTennesseeCourse.fulfilled,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'fulfilled'
          state.ceBrokerTennesseeOperationSuccess =
            'CEBroker Tennessee accreditation finalized successfully'
          let { courseId, finalized } = action.payload
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.tennessee.finalized =
              finalized
            state.courseAccreditations[courseId].ceBroker.tennessee.pending =
              undefined
          }
        },
      )
      .addCase(
        finalizePendingCEBrokerTennesseeCourse.rejected,
        (state, action) => {
          state.ceBrokerTennesseeOperationStatus = 'rejected'
          state.ceBrokerTennesseeOperationError =
            action.error.message ||
            'Failed to finalize CEBroker Tennessee. Ensure the status is Approved first.'
        },
      )
      // ── Course-side CEBroker Florida ───────────────────────────────────────
      .addCase(createPendingCEBrokerFloridaCourse.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(
        createPendingCEBrokerFloridaCourse.fulfilled,
        (state, action) => {
          state.ceBrokerFloridaOperationStatus = 'fulfilled'
          state.ceBrokerFloridaOperationSuccess =
            'CEBroker Florida accreditation created successfully'
          let { courseId } = action.meta.arg
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.florida.pending =
              action.payload
          }
        },
      )
      .addCase(createPendingCEBrokerFloridaCourse.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to create CEBroker Florida accreditation'
      })
      .addCase(updatePendingCEBrokerFloridaCourse.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(
        updatePendingCEBrokerFloridaCourse.fulfilled,
        (state, action) => {
          state.ceBrokerFloridaOperationStatus = 'fulfilled'
          let { courseId, status } = action.meta.arg
          state.ceBrokerFloridaOperationSuccess =
            status === 'Submitted'
              ? 'Submitted for approval successfully'
              : status === 'Approved'
                ? 'Approved successfully'
                : 'CEBroker Florida accreditation updated successfully'
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.florida.pending =
              action.payload
          }
        },
      )
      .addCase(updatePendingCEBrokerFloridaCourse.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to update CEBroker Florida accreditation'
      })
      .addCase(deletePendingCEBrokerFloridaCourse.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(
        deletePendingCEBrokerFloridaCourse.fulfilled,
        (state, action) => {
          state.ceBrokerFloridaOperationStatus = 'fulfilled'
          state.ceBrokerFloridaOperationSuccess =
            'CEBroker Florida accreditation deleted successfully'
          let { courseId } = action.payload
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.florida.pending =
              undefined
          }
        },
      )
      .addCase(deletePendingCEBrokerFloridaCourse.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to delete CEBroker Florida accreditation'
      })
      .addCase(finalizePendingCEBrokerFloridaCourse.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(
        finalizePendingCEBrokerFloridaCourse.fulfilled,
        (state, action) => {
          state.ceBrokerFloridaOperationStatus = 'fulfilled'
          state.ceBrokerFloridaOperationSuccess =
            'CEBroker Florida accreditation finalized successfully'
          let { courseId, finalized } = action.payload
          if (state.courseAccreditations[courseId]) {
            state.courseAccreditations[courseId].ceBroker.florida.finalized =
              finalized
            state.courseAccreditations[courseId].ceBroker.florida.pending =
              undefined
          }
        },
      )
      .addCase(
        finalizePendingCEBrokerFloridaCourse.rejected,
        (state, action) => {
          state.ceBrokerFloridaOperationStatus = 'rejected'
          state.ceBrokerFloridaOperationError =
            action.error.message ||
            'Failed to finalize CEBroker Florida. Ensure the status is Approved first.'
        },
      )
      // ── Course-side New Jersey ─────────────────────────────────────────────
      .addCase(createPendingNewJerseyCourse.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(createPendingNewJerseyCourse.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation created successfully'
        let { courseId } = action.meta.arg
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newJersey.pending =
            action.payload
        }
      })
      .addCase(createPendingNewJerseyCourse.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to create New Jersey accreditation'
      })
      .addCase(updatePendingNewJerseyCourse.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(updatePendingNewJerseyCourse.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        let { courseId, status } = action.meta.arg
        state.newJerseyOperationSuccess =
          status === 'Submitted'
            ? 'Submitted for approval successfully'
            : status === 'Approved'
              ? 'Approved successfully'
              : 'New Jersey accreditation updated successfully'
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newJersey.pending =
            action.payload
        }
      })
      .addCase(updatePendingNewJerseyCourse.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to update New Jersey accreditation'
      })
      .addCase(deletePendingNewJerseyCourse.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(deletePendingNewJerseyCourse.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newJersey.pending = undefined
        }
      })
      .addCase(deletePendingNewJerseyCourse.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to delete New Jersey accreditation'
      })
      .addCase(finalizePendingNewJerseyCourse.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(finalizePendingNewJerseyCourse.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation finalized successfully'
        let { courseId, finalized } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newJersey.finalized = finalized
          state.courseAccreditations[courseId].newJersey.pending = undefined
        }
      })
      .addCase(finalizePendingNewJerseyCourse.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message ||
          'Failed to finalize New Jersey. Ensure the status is Approved first.'
      })
      // ── Course-side New York ───────────────────────────────────────────────
      .addCase(createPendingNewYorkCourse.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(createPendingNewYorkCourse.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation created successfully'
        let { courseId } = action.meta.arg
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newYork.pending = action.payload
        }
      })
      .addCase(createPendingNewYorkCourse.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to create New York accreditation'
      })
      .addCase(updatePendingNewYorkCourse.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(updatePendingNewYorkCourse.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        let { courseId, status } = action.meta.arg
        state.newYorkOperationSuccess =
          status === 'Submitted'
            ? 'Submitted for approval successfully'
            : status === 'Approved'
              ? 'Approved successfully'
              : 'New York accreditation updated successfully'
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newYork.pending = action.payload
        }
      })
      .addCase(updatePendingNewYorkCourse.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to update New York accreditation'
      })
      .addCase(deletePendingNewYorkCourse.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(deletePendingNewYorkCourse.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newYork.pending = undefined
        }
      })
      .addCase(deletePendingNewYorkCourse.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to delete New York accreditation'
      })
      .addCase(finalizePendingNewYorkCourse.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(finalizePendingNewYorkCourse.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation finalized successfully'
        let { courseId, finalized } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newYork.finalized = finalized
          state.courseAccreditations[courseId].newYork.pending = undefined
        }
      })
      .addCase(finalizePendingNewYorkCourse.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message ||
          'Failed to finalize New York. Ensure the status is Approved first.'
      })
      // ── Delete finalized accreditations ────────────────────────────────────
      .addCase(deleteANCCEvent.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(deleteANCCEvent.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ancc.finalized = undefined
        }
      })
      .addCase(deleteANCCEvent.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to delete ANCC accreditation'
      })
      .addCase(deleteANCCCourse.pending, (state) => {
        state.anccOperationStatus = 'pending'
        state.anccOperationError = null
        state.anccOperationSuccess = null
      })
      .addCase(deleteANCCCourse.fulfilled, (state, action) => {
        state.anccOperationStatus = 'fulfilled'
        state.anccOperationSuccess = 'ANCC accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ancc.finalized = undefined
        }
      })
      .addCase(deleteANCCCourse.rejected, (state, action) => {
        state.anccOperationStatus = 'rejected'
        state.anccOperationError =
          action.error.message || 'Failed to delete ANCC accreditation'
      })
      .addCase(deleteCAPCE.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(deleteCAPCE.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess = 'CAPCE accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].capce.finalized = undefined
        }
      })
      .addCase(deleteCAPCE.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to delete CAPCE accreditation'
      })
      .addCase(deleteCAPCECourse.pending, (state) => {
        state.capceOperationStatus = 'pending'
        state.capceOperationError = null
        state.capceOperationSuccess = null
      })
      .addCase(deleteCAPCECourse.fulfilled, (state, action) => {
        state.capceOperationStatus = 'fulfilled'
        state.capceOperationSuccess = 'CAPCE accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].capce.finalized = undefined
        }
      })
      .addCase(deleteCAPCECourse.rejected, (state, action) => {
        state.capceOperationStatus = 'rejected'
        state.capceOperationError =
          action.error.message || 'Failed to delete CAPCE accreditation'
      })
      .addCase(deleteVirginia.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(deleteVirginia.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].virginia.finalized = undefined
        }
      })
      .addCase(deleteVirginia.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to delete Virginia accreditation'
      })
      .addCase(deleteVirginiaCourse.pending, (state) => {
        state.virginiaOperationStatus = 'pending'
        state.virginiaOperationError = null
        state.virginiaOperationSuccess = null
      })
      .addCase(deleteVirginiaCourse.fulfilled, (state, action) => {
        state.virginiaOperationStatus = 'fulfilled'
        state.virginiaOperationSuccess =
          'Virginia accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].virginia.finalized = undefined
        }
      })
      .addCase(deleteVirginiaCourse.rejected, (state, action) => {
        state.virginiaOperationStatus = 'rejected'
        state.virginiaOperationError =
          action.error.message || 'Failed to delete Virginia accreditation'
      })
      .addCase(deleteCEBrokerKentucky.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(deleteCEBrokerKentucky.fulfilled, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'fulfilled'
        state.ceBrokerKentuckyOperationSuccess =
          'CEBroker Kentucky accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.kentucky.finalized =
            undefined
        }
      })
      .addCase(deleteCEBrokerKentucky.rejected, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'rejected'
        state.ceBrokerKentuckyOperationError =
          action.error.message ||
          'Failed to delete CEBroker Kentucky accreditation'
      })
      .addCase(deleteCEBrokerKentuckyCourse.pending, (state) => {
        state.ceBrokerKentuckyOperationStatus = 'pending'
        state.ceBrokerKentuckyOperationError = null
        state.ceBrokerKentuckyOperationSuccess = null
      })
      .addCase(deleteCEBrokerKentuckyCourse.fulfilled, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'fulfilled'
        state.ceBrokerKentuckyOperationSuccess =
          'CEBroker Kentucky accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ceBroker.kentucky.finalized =
            undefined
        }
      })
      .addCase(deleteCEBrokerKentuckyCourse.rejected, (state, action) => {
        state.ceBrokerKentuckyOperationStatus = 'rejected'
        state.ceBrokerKentuckyOperationError =
          action.error.message ||
          'Failed to delete CEBroker Kentucky accreditation'
      })
      .addCase(deleteCEBrokerTennessee.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(deleteCEBrokerTennessee.fulfilled, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'fulfilled'
        state.ceBrokerTennesseeOperationSuccess =
          'CEBroker Tennessee accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.tennessee.finalized =
            undefined
        }
      })
      .addCase(deleteCEBrokerTennessee.rejected, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'rejected'
        state.ceBrokerTennesseeOperationError =
          action.error.message ||
          'Failed to delete CEBroker Tennessee accreditation'
      })
      .addCase(deleteCEBrokerTennesseeCourse.pending, (state) => {
        state.ceBrokerTennesseeOperationStatus = 'pending'
        state.ceBrokerTennesseeOperationError = null
        state.ceBrokerTennesseeOperationSuccess = null
      })
      .addCase(deleteCEBrokerTennesseeCourse.fulfilled, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'fulfilled'
        state.ceBrokerTennesseeOperationSuccess =
          'CEBroker Tennessee accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ceBroker.tennessee.finalized =
            undefined
        }
      })
      .addCase(deleteCEBrokerTennesseeCourse.rejected, (state, action) => {
        state.ceBrokerTennesseeOperationStatus = 'rejected'
        state.ceBrokerTennesseeOperationError =
          action.error.message ||
          'Failed to delete CEBroker Tennessee accreditation'
      })
      .addCase(deleteCEBrokerFlorida.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(deleteCEBrokerFlorida.fulfilled, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'fulfilled'
        state.ceBrokerFloridaOperationSuccess =
          'CEBroker Florida accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].ceBroker.florida.finalized =
            undefined
        }
      })
      .addCase(deleteCEBrokerFlorida.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to delete CEBroker Florida accreditation'
      })
      .addCase(deleteCEBrokerFloridaCourse.pending, (state) => {
        state.ceBrokerFloridaOperationStatus = 'pending'
        state.ceBrokerFloridaOperationError = null
        state.ceBrokerFloridaOperationSuccess = null
      })
      .addCase(deleteCEBrokerFloridaCourse.fulfilled, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'fulfilled'
        state.ceBrokerFloridaOperationSuccess =
          'CEBroker Florida accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].ceBroker.florida.finalized =
            undefined
        }
      })
      .addCase(deleteCEBrokerFloridaCourse.rejected, (state, action) => {
        state.ceBrokerFloridaOperationStatus = 'rejected'
        state.ceBrokerFloridaOperationError =
          action.error.message ||
          'Failed to delete CEBroker Florida accreditation'
      })
      .addCase(deleteNewJersey.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(deleteNewJersey.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newJersey.finalized = undefined
        }
      })
      .addCase(deleteNewJersey.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to delete New Jersey accreditation'
      })
      .addCase(deleteNewJerseyCourse.pending, (state) => {
        state.newJerseyOperationStatus = 'pending'
        state.newJerseyOperationError = null
        state.newJerseyOperationSuccess = null
      })
      .addCase(deleteNewJerseyCourse.fulfilled, (state, action) => {
        state.newJerseyOperationStatus = 'fulfilled'
        state.newJerseyOperationSuccess =
          'New Jersey accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newJersey.finalized = undefined
        }
      })
      .addCase(deleteNewJerseyCourse.rejected, (state, action) => {
        state.newJerseyOperationStatus = 'rejected'
        state.newJerseyOperationError =
          action.error.message || 'Failed to delete New Jersey accreditation'
      })
      .addCase(deleteNewYork.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(deleteNewYork.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation deleted successfully'
        let { eventId } = action.payload
        if (state.eventAccreditations[eventId]) {
          state.eventAccreditations[eventId].newYork.finalized = undefined
        }
      })
      .addCase(deleteNewYork.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to delete New York accreditation'
      })
      .addCase(deleteNewYorkCourse.pending, (state) => {
        state.newYorkOperationStatus = 'pending'
        state.newYorkOperationError = ''
        state.newYorkOperationSuccess = ''
      })
      .addCase(deleteNewYorkCourse.fulfilled, (state, action) => {
        state.newYorkOperationStatus = 'fulfilled'
        state.newYorkOperationSuccess =
          'New York accreditation deleted successfully'
        let { courseId } = action.payload
        if (state.courseAccreditations[courseId]) {
          state.courseAccreditations[courseId].newYork.finalized = undefined
        }
      })
      .addCase(deleteNewYorkCourse.rejected, (state, action) => {
        state.newYorkOperationStatus = 'rejected'
        state.newYorkOperationError =
          action.error.message || 'Failed to delete New York accreditation'
      })
  },
})

// ─── Actions ──────────────────────────────────────────────────────────────────

export let {
  clearAnccOperationStatus,
  clearCapceOperationStatus,
  clearVirginiaOperationStatus,
  clearCEBrokerKentuckyOperationStatus,
  clearCEBrokerTennesseeOperationStatus,
  clearCEBrokerFloridaOperationStatus,
  clearNewJerseyOperationStatus,
  clearNewYorkOperationStatus,
} = accreditationSlice.actions

// ─── Selectors ────────────────────────────────────────────────────────────────

export let selectEventAccreditations = (state: AppRootState) =>
  state.accreditation.eventAccreditations
export let selectEventAccreditationsStatus = (state: AppRootState) =>
  state.accreditation.eventAccreditationsStatus
export let selectEventAccreditationsByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.accreditation.eventAccreditations[eventID]
export let selectEventAccreditationsStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.accreditation.eventAccreditationsStatus[eventID]

export let selectCapceOperationStatus = (state: AppRootState) =>
  state.accreditation.capceOperationStatus
export let selectCapceOperationError = (state: AppRootState) =>
  state.accreditation.capceOperationError
export let selectCapceOperationSuccess = (state: AppRootState) =>
  state.accreditation.capceOperationSuccess

export let selectVirginiaOperationStatus = (state: AppRootState) =>
  state.accreditation.virginiaOperationStatus
export let selectVirginiaOperationError = (state: AppRootState) =>
  state.accreditation.virginiaOperationError
export let selectVirginiaOperationSuccess = (state: AppRootState) =>
  state.accreditation.virginiaOperationSuccess

export let selectCEBrokerKentuckyOperationStatus = (state: AppRootState) =>
  state.accreditation.ceBrokerKentuckyOperationStatus
export let selectCEBrokerKentuckyOperationError = (state: AppRootState) =>
  state.accreditation.ceBrokerKentuckyOperationError
export let selectCEBrokerKentuckyOperationSuccess = (state: AppRootState) =>
  state.accreditation.ceBrokerKentuckyOperationSuccess

export let selectCEBrokerTennesseeOperationStatus = (state: AppRootState) =>
  state.accreditation.ceBrokerTennesseeOperationStatus
export let selectCEBrokerTennesseeOperationError = (state: AppRootState) =>
  state.accreditation.ceBrokerTennesseeOperationError
export let selectCEBrokerTennesseeOperationSuccess = (state: AppRootState) =>
  state.accreditation.ceBrokerTennesseeOperationSuccess

export let selectCEBrokerFloridaOperationStatus = (state: AppRootState) =>
  state.accreditation.ceBrokerFloridaOperationStatus
export let selectCEBrokerFloridaOperationError = (state: AppRootState) =>
  state.accreditation.ceBrokerFloridaOperationError
export let selectCEBrokerFloridaOperationSuccess = (state: AppRootState) =>
  state.accreditation.ceBrokerFloridaOperationSuccess

export let selectNewJerseyOperationStatus = (state: AppRootState) =>
  state.accreditation.newJerseyOperationStatus
export let selectNewJerseyOperationError = (state: AppRootState) =>
  state.accreditation.newJerseyOperationError
export let selectNewJerseyOperationSuccess = (state: AppRootState) =>
  state.accreditation.newJerseyOperationSuccess

export let selectNewYorkOperationStatus = (state: AppRootState) =>
  state.accreditation.newYorkOperationStatus
export let selectNewYorkOperationError = (state: AppRootState) =>
  state.accreditation.newYorkOperationError
export let selectNewYorkOperationSuccess = (state: AppRootState) =>
  state.accreditation.newYorkOperationSuccess

export let selectCourseAccreditations = (state: AppRootState) =>
  state.accreditation.courseAccreditations
export let selectCourseAccreditationsByCourseID =
  (courseID: string) => (state: AppRootState) =>
    state.accreditation.courseAccreditations[courseID]
export let selectCourseAccreditationsStatusByCourseID =
  (courseID: string) => (state: AppRootState) =>
    state.accreditation.courseAccreditationsStatus[courseID]

export let selectV1MigrationEntries = (state: AppRootState) =>
  state.accreditation.v1MigrationEntries
export let selectV1MigrationStatus = (state: AppRootState) =>
  state.accreditation.v1MigrationStatus

export let selectAnccOperationStatus = (state: AppRootState) =>
  state.accreditation.anccOperationStatus
export let selectAnccOperationError = (state: AppRootState) =>
  state.accreditation.anccOperationError
export let selectAnccOperationSuccess = (state: AppRootState) =>
  state.accreditation.anccOperationSuccess

export default accreditationSlice.reducer
